import { ApiError, http } from './client'
import type {
  DependencyGraphResponse,
  FeedbackVerdict,
  FindingsListResponse,
  LiveScansResponse,
  PendingScansListResponse,
  PostScanRequest,
  ScanCreatedResponse,
  ScanReportResponse,
  ScanSourcesResponse,
  ScanListParams,
  ScanStatusResponse,
  ScansListResponse,
  ScanVisibility,
  WhatHappenedResponse,
} from '../types/api'
import type { ScanAgentsResponse } from '../types/agents'

const BASE = '/api/v1/scans'

export const scansApi = {
  create: (body: PostScanRequest): Promise<ScanCreatedResponse> =>
    http.post(BASE, body),

  /** DEC-191: filtered, paged and counted server-side. Empty values are left out. */
  list: (params?: ScanListParams): Promise<ScansListResponse> => {
    const qs = new URLSearchParams()
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value === undefined || value === null || value === '') continue
      qs.set(key, String(value))
    }
    const q = qs.toString()
    return http.get(`${BASE}${q ? `?${q}` : ''}`)
  },

  /** DEC-191: live progress of every queued, running or paused scan, plus counts. */
  live: (): Promise<LiveScansResponse> => http.get(`${BASE}/live`),

  /** DEC-191: deferred requests that have not started yet. */
  pending: (): Promise<PendingScansListResponse> => http.get(`${BASE}/pending`),

  /** DEC-191: cancel a deferred request. 409 once the scheduler is starting it. */
  cancelPending: (requestId: string): Promise<void> =>
    http.delete(`${BASE}/pending/${requestId}`),

  get: (scanId: string): Promise<ScanStatusResponse> =>
    http.get(`${BASE}/${scanId}`),

  findings: (
    scanId: string,
    params?: {
      category?: string
      severity?: string
      min_confidence?: number
      limit?: number
      offset?: number
    },
  ): Promise<FindingsListResponse> => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.severity) qs.set('severity', params.severity)
    if (params?.min_confidence !== undefined)
      qs.set('min_confidence', String(params.min_confidence))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    if (params?.offset !== undefined) qs.set('offset', String(params.offset))
    const q = qs.toString()
    return http.get(`${BASE}/${scanId}/findings${q ? `?${q}` : ''}`)
  },

  report: (scanId: string): Promise<ScanReportResponse> =>
    http.get(`${BASE}/${scanId}/report`),

  /** DEC-185: a finished scan's agents, rebuilt from saved data. 409 while it runs. */
  agents: (scanId: string): Promise<ScanAgentsResponse> =>
    http.get(`${BASE}/${scanId}/agents`),

  deleteScan: (scanId: string): Promise<void> =>
    http.delete(`${BASE}/${scanId}`),

  /** DEC-192: take a scan out of the default lists. Keeps everything and its state. */
  archive: (scanId: string): Promise<ScanStatusResponse> =>
    http.post(`${BASE}/${scanId}/archive`, {}),

  /** DEC-192: put an archived scan back in the main lists. */
  unarchive: (scanId: string): Promise<ScanStatusResponse> =>
    http.post(`${BASE}/${scanId}/unarchive`, {}),

  /** DEC-192: delete permanently. Erases the findings, keeps a record in Archived. */
  purge: (scanId: string): Promise<ScanStatusResponse> =>
    http.post(`${BASE}/${scanId}/purge`, {}),

  graph: (scanId: string): Promise<DependencyGraphResponse> =>
    http.get(`${BASE}/${scanId}/graph`),

  pause: (scanId: string): Promise<{ scan_id: string; state: string }> =>
    http.post(`${BASE}/${scanId}/pause`, {}),

  resume: (
    scanId: string,
    staticOnly = false,
  ): Promise<{ scan_id: string; state: string; static_only?: boolean }> =>
    http.post(`${BASE}/${scanId}/resume`, staticOnly ? { static_only: true } : {}),

  /** DEC-094: approve/decline a Tier-2 round-cap-extension decision for one agent.
   * Idempotent server-side — safe to resubmit. */
  submitRoundDecision: (
    scanId: string,
    agentId: string,
    approve: boolean,
  ): Promise<{ scan_id: string; agent_id: string; decision_id: string; status: string; granted_rounds: number }> =>
    http.post(`${BASE}/${scanId}/agents/${agentId}/round-decision`, { approve }),

  submitFeedback: (
    scanId: string,
    findingId: string,
    body: { verdict: FeedbackVerdict; notes?: string },
  ): Promise<{ feedback_id: string; finding_id: string; verdict: string; created_at: string }> =>
    http.post(`${BASE}/${scanId}/findings/${findingId}/feedback`, body),

  deleteFeedback: (scanId: string, findingId: string): Promise<void> =>
    http.delete(`${BASE}/${scanId}/findings/${findingId}/feedback`),

  /** DEC-196: share a private scan with the workspace, or make it private again. Owner only. */
  setVisibility: (scanId: string, visibility: ScanVisibility): Promise<ScanStatusResponse> =>
    http.patch(`${BASE}/${scanId}/visibility`, { visibility }),

  /** DEC-197: the story of a failed or partial scan. */
  whatHappened: (scanId: string): Promise<WhatHappenedResponse> =>
    http.get(`${BASE}/${scanId}/what-happened`),

  /** DEC-180: which source options work on this server, and the upload limits. */
  sources: (): Promise<ScanSourcesResponse> => http.get(`${BASE}/sources`),

  upload: uploadFolder,
}

/**
 * POST /api/v1/scans/upload (DEC-180).
 *
 * XMLHttpRequest rather than `http`, for two reasons: `http` forces a JSON content type,
 * which would break the multipart boundary, and `fetch` cannot report upload progress.
 * The session cookie travels the same way (`withCredentials`), and a 401 is handled the
 * same way as everywhere else.
 */
function uploadFolder(
  form: FormData,
  options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<ScanCreatedResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE}/upload`)
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      let body: unknown = null
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        body = null
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as ScanCreatedResponse)
        return
      }
      if (xhr.status === 401) window.location.href = '/login'
      const detail = (body as { detail?: unknown } | null)?.detail
      reject(
        new ApiError(
          xhr.status,
          typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : xhr.statusText,
        ),
      )
    }
    xhr.onerror = () => reject(new ApiError(0, 'The upload could not reach the server.'))
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'))
    options.signal?.addEventListener('abort', () => xhr.abort())
    xhr.send(form)
  })
}
